import { Component } from "@theme/component";
import { CartGroupedSections, CartUpdateEvent, ThemeEvents } from "@theme/events";
import { morphSection } from "@theme/section-renderer";
import { fetchConfig } from "@theme/utilities";

/**
 * Automatisches Geschenk ab Bestellwert — mit Ablehnrecht.
 *
 * Liegt der Warenkorbwert (OHNE das Geschenk selbst) auf oder ueber der
 * Schwelle, wandert das Geschenk hinein; faellt er darunter, fliegt es wieder
 * raus. Menge wird auf 1 begrenzt.
 *
 * Warum der Wert ohne Geschenk gerechnet wird: Traegt das Geschenk einen Preis,
 * wuerde es sich sonst selbst ueber die Schwelle heben — ein Warenkorb knapp
 * unter der Grenze bekaeme das Geschenk und behielte es. Genau dort sitzt der
 * Geldfehler.
 *
 * ---------------------------------------------------------------------------
 * NEU (23.08.2026): Der Kunde darf das Geschenk entfernen.
 *
 * Vorher legte die Automatik es sofort wieder hinein: Entfernen aendert den
 * Warenkorb, das loest `cartUpdate` aus, die Pruefung lief, der Wert lag immer
 * noch ueber der Schwelle — Geschenk wieder da. Fuer den Kunden sah es aus, als
 * taete der Entfernen-Knopf nichts.
 *
 * Geloest ueber ZWEI Warenkorb-Attribute (serverseitig, ueberleben Neuladen,
 * Flaechenwechsel und die ganze Sitzung):
 *
 *   geschenk_gelegt    = "ja"  -> WIR haben es hineingelegt und es lag zuletzt drin.
 *   geschenk_abgelehnt = "ja"  -> Der Kunde hat es entfernt. Nicht mehr nachlegen.
 *
 * Daraus faellt die Unterscheidung "Kunde hat entfernt" vs. "war noch nie drin"
 * ohne Ereignis-Schnueffelei: Liegt der Wert ueber der Schwelle, ist keine
 * Geschenkzeile da, und `geschenk_gelegt` steht auf "ja", dann lag es drin und
 * ist weg — und WIR haben es nicht entfernt, denn wir entfernen ausschliesslich
 * unterhalb der Schwelle. Also war es der Kunde.
 *
 * Faellt der Warenkorb unter die Schwelle, werden BEIDE Merker geloescht:
 * neuer Warenkorb, neue Chance. Wer wieder darueber kommt, bekommt das Geschenk
 * erneut angeboten — sonst waere die Ablehnung eine Einbahnstrasse.
 *
 * Zurueck holen kann der Kunde es ueber den Knopf in der Fortschrittsleiste
 * (`snippets/sidecart-fortschritt.liquid`, `[data-sidecart-geschenk-zurueck]`).
 * Ohne diesen Knopf muesste er erst unter die Schwelle fallen — das waere die
 * Sackgasse, die wir gerade vermeiden wollen.
 *
 * ---------------------------------------------------------------------------
 * ⚠️ SHOPIFY SPALTET DIE GESCHENKPOSITION.
 * Sobald der Rabatt "Kaufe X, bekomme Y" greift und mehr als ein Exemplar im
 * Korb liegt, entstehen ZWEI Zeilen mit derselben Varianten-ID: eine gratis
 * (das Y, `total_discount > 0`) und eine bezahlte daneben. Gemessen am
 * 23.08.2026 mit 3 Handtuechern: `menge=2 rabatt=0` + `menge=1 rabatt=250`.
 * Deshalb steht hier ueberall `filter()` und nirgends `find()` — ein `find()`
 * erwischt die falsche der beiden Zeilen und der Mengenwaechter setzt dann die
 * BEZAHLTE auf 1, statt sie zu entfernen.
 *
 * Reihenfolge der Schutzmassnahmen gegen Endlosschleifen:
 *   1. `#laeuft` sperrt waehrend einer laufenden Aenderung.
 *   2. Jeder Durchlauf macht HOECHSTENS EINE Aenderung und kehrt dann zurueck.
 *      Die naechste Runde kommt ueber `cartUpdate` von selbst. Jede Pruefung
 *      ist idempotent, also laeuft die Kette in den Ruhezustand statt zu kreisen.
 */

const MERKER_GELEGT = "geschenk_gelegt";
const MERKER_ABGELEHNT = "geschenk_abgelehnt";

class SidecartGeschenk extends Component {
  #laeuft = false;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(ThemeEvents.cartUpdate, this.#beiAenderung);
    // Der Zurueck-Knopf sitzt in der Fortschrittsleiste, also ausserhalb dieses
    // Elements. Deshalb am Dokument lauschen statt am eigenen Baum.
    document.addEventListener("click", this.#beiKlick);
    // Auch beim ersten Laden pruefen: der Warenkorb kann aus einer frueheren
    // Sitzung stammen, in der die Schwelle noch nicht galt. Dieser Weg liest
    // dieselben Merker wie `cartUpdate` — sonst kaeme das abgelehnte Geschenk
    // beim naechsten Seitenaufruf zurueck.
    this.#pruefen();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(ThemeEvents.cartUpdate, this.#beiAenderung);
    document.removeEventListener("click", this.#beiKlick);
  }

  #beiAenderung = () => {
    this.#pruefen();
  };

  #beiKlick = (ereignis) => {
    const knopf = ereignis.target?.closest?.("[data-sidecart-geschenk-zurueck]");
    if (!knopf) return;
    ereignis.preventDefault();
    this.#zurueckholen();
  };

  get #varianteId() {
    return Number(this.dataset.variante);
  }

  get #schwelle() {
    return Number(this.dataset.schwelle);
  }

  get #verfuegbar() {
    return this.dataset.verfuegbar !== "false";
  }

  /**
   * Varianten-IDs der Dropshipping-Zeilen im aktuellen Warenkorb.
   *
   * Cart-Line-Items tragen den Produkt-Tag nicht nativ; das Signal kommt
   * serverseitig aus snippets/sidecart-geschenk.liquid (Produkt-Tag
   * "Dropshipping") als `data-ds-varianten="[…]"`. Das Element wird bei jeder
   * Warenkorb-Aenderung mit der cart-drawer-Section neu gemorpht, also ist die
   * Liste beim naechsten `cartUpdate` immer frisch. Defensiv geparst: kaputtes
   * oder fehlendes Attribut -> leere Menge (Verhalten wie vor dem DS-Ausschluss).
   */
  get #dsVarianten() {
    try {
      const roh = this.dataset.dsVarianten;
      if (!roh) return new Set();
      const liste = JSON.parse(roh);
      if (!Array.isArray(liste)) return new Set();
      return new Set(liste.map(Number));
    } catch {
      return new Set();
    }
  }

  async #warenkorbLesen() {
    return (await fetch(window.FoxTheme.routes.cart)).json();
  }

  /** Alle Zeilen mit der Geschenk-Variante. Siehe Hinweis zur Spaltung oben. */
  #geschenkZeilen(warenkorb) {
    return warenkorb.items.filter((i) => Number(i.variant_id) === this.#varianteId);
  }

  async #pruefen() {
    if (this.#laeuft) return;
    if (!this.#varianteId || !this.#schwelle) return;

    this.#laeuft = true;
    try {
      const warenkorb = await this.#warenkorbLesen();
      const zeilen = this.#geschenkZeilen(warenkorb);
      const menge = zeilen.reduce((summe, z) => summe + z.quantity, 0);

      // Wert ohne das Geschenk UND ohne Dropshipping-Zeilen. Der erste `filter`
      // schliesst beide Geschenk-Zeilen aus (auch die bezahlte), der zweite die
      // DS-Zeilen. So rechnet die Schwelle nur gegen den Nicht-Dropshipping-
      // Warenwert — deckungsgleich mit der FREEGIFT-Rabattbedingung. Reiner
      // DS-Warenkorb: Nicht-DS-Wert 0 < Schwelle -> Geschenk kommt nicht (und
      // wird entfernt, falls drin). Muss identisch zur Rechnung in
      // snippets/sidecart-fortschritt.liquid sein.
      const dsIds = this.#dsVarianten;
      const wert = warenkorb.items
        .filter((i) => Number(i.variant_id) !== this.#varianteId)
        .filter((i) => !dsIds.has(Number(i.variant_id)))
        .reduce((summe, i) => summe + i.final_line_price, 0);

      const berechtigt = wert >= this.#schwelle;
      const merker = warenkorb.attributes || {};
      const abgelehnt = merker[MERKER_ABGELEHNT] === "ja";
      const gelegt = merker[MERKER_GELEGT] === "ja";

      // ---- unter der Schwelle -------------------------------------------
      if (!berechtigt) {
        if (zeilen.length) {
          // Eine Zeile pro Runde, dann zurueck — der Rest kommt ueber das
          // naechste `cartUpdate`. Keys werden dadurch nie schal.
          await this.#aendern(this.#mengeNutzlast(zeilen[0].key, 0));
          return;
        }
        // Neuer Warenkorb, neue Chance: Merker zuruecksetzen.
        if (abgelehnt || gelegt) {
          await this.#merken({ [MERKER_ABGELEHNT]: "", [MERKER_GELEGT]: "" }, { anzeige: abgelehnt });
        }
        return;
      }

      // ---- ueber der Schwelle, kein Geschenk im Korb ----------------------
      if (!zeilen.length) {
        if (gelegt) {
          // Es lag drin, jetzt nicht mehr — und wir waren es nicht.
          await this.#merken({ [MERKER_ABGELEHNT]: "ja", [MERKER_GELEGT]: "" }, { anzeige: true });
          return;
        }
        if (abgelehnt) return; // Wunsch des Kunden. Nichts tun.
        if (!this.#verfuegbar) return; // ausverkauft: still nichts tun
        await this.#aendern(this.#hinzufuegenNutzlast());
        await this.#merken({ [MERKER_GELEGT]: "ja" });
        return;
      }

      // ---- ueber der Schwelle, Geschenk liegt drin ------------------------
      if (abgelehnt) {
        // Der Kunde hat es selbst zurueckgeholt (Knopf oder Produktseite).
        await this.#merken({ [MERKER_ABGELEHNT]: "", [MERKER_GELEGT]: "ja" }, { anzeige: true });
        return;
      }
      if (!gelegt) {
        await this.#merken({ [MERKER_GELEGT]: "ja" });
        return;
      }
      if (menge !== 1) await this.#aufEinsBringen(zeilen);
    } catch (fehler) {
      console.error("Sidecart-Geschenk:", fehler);
    } finally {
      this.#laeuft = false;
    }
  }

  /**
   * Es bleibt bei EINEM Geschenk. Liegt eine rabattierte Zeile vor, fliegen
   * zuerst die BEZAHLTEN Zeilen raus — nicht die gratis. Erst wenn keine
   * rabattierte dabei ist, wird stumpf auf Menge 1 gesetzt.
   */
  async #aufEinsBringen(zeilen) {
    const rabattiert = zeilen.filter((z) => z.total_discount > 0);
    const bezahlt = zeilen.filter((z) => z.total_discount === 0);
    if (rabattiert.length && bezahlt.length) {
      await this.#aendern(this.#mengeNutzlast(bezahlt[0].key, 0));
      return;
    }
    const behalten = rabattiert[0] ?? zeilen[0];
    const ueberzaehlig = zeilen.find((z) => z.key !== behalten.key);
    if (ueberzaehlig) {
      await this.#aendern(this.#mengeNutzlast(ueberzaehlig.key, 0));
      return;
    }
    if (behalten.quantity !== 1) await this.#aendern(this.#mengeNutzlast(behalten.key, 1));
  }

  /** Knopf „Geschenk hinzufuegen" in der Fortschrittsleiste. */
  async #zurueckholen() {
    if (this.#laeuft) return;
    if (!this.#varianteId || !this.#verfuegbar) return;
    this.#laeuft = true;
    try {
      await this.#merken({ [MERKER_ABGELEHNT]: "" });
      await this.#aendern(this.#hinzufuegenNutzlast());
      await this.#merken({ [MERKER_GELEGT]: "ja" });
    } catch (fehler) {
      console.error("Sidecart-Geschenk:", fehler);
    } finally {
      this.#laeuft = false;
    }
  }

  #hinzufuegenNutzlast() {
    return {
      url: window.FoxTheme.routes.cart_add_url,
      body: { items: [{ id: this.#varianteId, quantity: 1 }] },
    };
  }

  #mengeNutzlast(key, menge) {
    return {
      url: window.FoxTheme.routes.cart_change_url,
      body: { id: key, quantity: menge },
    };
  }

  /**
   * Schreibt die Merker in die Warenkorb-Attribute. Ein leerer Wert loescht
   * das Attribut — so bleibt der Warenkorb sauber, wenn nichts abgelehnt ist.
   * `anzeige: true` zieht die Darstellung nach, weil der Zurueck-Knopf in der
   * Fortschrittsleiste an `geschenk_abgelehnt` haengt.
   */
  async #merken(attribute, { anzeige = false } = {}) {
    const antwort = await fetch(
      window.FoxTheme.routes.cart_update_url,
      fetchConfig("json", { body: JSON.stringify({ attributes: attribute }) })
    );
    if (!antwort.ok) {
      console.warn("Sidecart-Geschenk: Merker konnte nicht gesetzt werden", antwort.status);
      return;
    }
    if (anzeige) await this.#anzeigeNachziehen();
  }

  /**
   * Fuehrt die Warenkorb-Aenderung aus und bringt die Anzeige nach.
   * @param {{url: string, body: object}} nutzlast
   */
  async #aendern(nutzlast) {
    const antwort = await fetch(
      nutzlast.url,
      fetchConfig("json", { body: JSON.stringify(nutzlast.body) })
    );
    if (!antwort.ok) {
      // 422 heisst meist ausverkauft. Kein Abbruch mit Fehlermeldung an den
      // Kunden — das Geschenk ist eine Zugabe, kein Teil seiner Bestellung.
      console.warn("Sidecart-Geschenk: Warenkorb-Aenderung abgelehnt", antwort.status);
      return;
    }
    await this.#anzeigeNachziehen();
  }

  /**
   * Holt die betroffenen Sections neu und morpht sie — dasselbe Muster, das
   * das Theme fuer die Geschenkverpackung nutzt (assets/cart-items.js).
   */
  async #anzeigeNachziehen() {
    const ids = [];
    document.dispatchEvent(new CartGroupedSections(ids));
    const eindeutig = [...new Set(ids)];

    await Promise.all(
      eindeutig.map(async (sid) => {
        const url = `${window.location.pathname.split("?")[0]}?section_id=${sid}`;
        const html = await (await fetch(url)).text();
        await morphSection(sid, html);
      })
    );

    const warenkorb = await this.#warenkorbLesen();
    document.dispatchEvent(
      new CartUpdateEvent(warenkorb, "sidecart-geschenk", {
        itemCount: warenkorb.item_count || 0,
        source: "sidecart-geschenk",
      })
    );
  }
}

if (!customElements.get("sidecart-geschenk")) {
  customElements.define("sidecart-geschenk", SidecartGeschenk);
}
